import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'

import { BEIGE_400, CORAL_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { MUIInputField } from 'components/common/MUIInputField'
import { RealEstatePropertyListItemDTO } from 'models/property'

import type { PropertyCreateSource } from '../analytics/events'

import { PropertySearchList, PROPERTY_LISTBOX_ID } from './PropertySearchList.component'
import { usePropertySearchKeyboard } from './usePropertySearchKeyboard'
import { usePropertySearchResults } from './usePropertySearchResults'

/**
 * The property search's state. Held by the drawer rather than the box, so the text survives a trip
 * into "create new property" and back.
 */
export const usePropertySearchBox = (onSelect: (property: RealEstatePropertyListItemDTO) => void) => {
  const [searchText, setSearchText] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  // The same results the list renders (React Query dedupes), so the arrow keys can be handled in the input.
  const results = usePropertySearchResults(searchText, isFocused)

  const select = (property: RealEstatePropertyListItemDTO) => {
    onSelect(property)
    setSearchText('')
  }

  const keyboard = usePropertySearchKeyboard({
    optionCount: results.properties.length,
    isOpen: results.showResults,
    onSelect: (index) => {
      const property = results.properties[index]
      if (!property) return
      select(property)
      // Focus holds the list open, and Enter leaves it in the box; typing again reopens it.
      setIsFocused(false)
    },
  })

  return { searchText, setSearchText, isFocused, setIsFocused, results, keyboard, select }
}

interface Props {
  search: ReturnType<typeof usePropertySearchBox>
  onCreateNew: (source: PropertyCreateSource) => void
}

/** The property search box, its result list, and the "or create a new property" link. */
export const PropertySearchBox: React.FC<Props> = ({ search, onCreateNew }) => {
  const { t } = useTranslation(['order', 'purchase_flow_revamp'])
  const { searchText, setSearchText, isFocused, setIsFocused, results, keyboard, select } = search

  return (
    <Box sx={{ backgroundColor: BEIGE_400, borderRadius: '0.8rem', padding: '1.6rem' }}>

      <Box sx={{ position: 'relative', zIndex: 2 }}>

        <MUIInputField
          value={searchText}
          placeholder={t('order:step_address.search_property_placeholder')}
          startIcon={<SearchRoundedIcon sx={{ fontSize: '1.6rem' }} />}
          onChange={(e) => {
            setSearchText(e.target.value)
            setIsFocused(true)
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 150)}
          onKeyDown={keyboard.onKeyDown}
          autoComplete="off"
          inputAriaProps={{
            role: 'combobox',
            'aria-expanded': results.showResults,
            'aria-controls': PROPERTY_LISTBOX_ID,
            'aria-activedescendant': keyboard.activeDescendantId,
          }}
        />

        <PropertySearchList
          searchText={searchText}
          isSearchFocused={isFocused}
          onSelectProperty={(property) => {
            select(property)
            // The press kept focus in the input, so close the list and the phone keyboard here.
            setIsFocused(false)
            if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
          }}
          activeIndex={keyboard.activeIndex}
          onHoverIndex={keyboard.setActiveIndex}
          onCreateNew={() => onCreateNew('no_results')}
        />

      </Box>

      {/* Hidden while the list is open: it sits where the list lands, and the empty state has its own create row. */}
      {!results.showResults && !results.showEmpty &&
        <Box sx={{ pt: '0.8rem' }}>
          <Typography variant="text-sm" color={GRAY_700}>
            {t('purchase_flow_revamp:or_prefix')}
            <Typography
              component="span"
              variant="text-sm"
              color={GRAY_900}
              fontWeight="bold"
              sx={{ cursor: 'pointer', '&:hover': { color: CORAL_600 } }}
              onClick={() => onCreateNew('link')}
            >
              {t('purchase_flow_revamp:create_new_property_link')}
            </Typography>
          </Typography>
        </Box>
      }

    </Box>
  )
}
